import type { OrganizationType, SubscriptionType } from '@kanbu/schema';
import { Currency, SubscriptionTier } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label, toast } from '@utima/ui';
import { useState } from 'react';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';
import {
  AutoRenewalSettings,
  type AutoRenewalUpdateValues,
} from '@/features/organizations/components/AutoRenewalSettings';
import { UsageMetricsDisplay } from '@/features/organizations/components/UsageMetricsDisplay';
import { trpc } from '@/services/trpc';

type BalanceAndTopUpCardProps = {
  organizationId: string;
  organization: OrganizationType;
  subscription: SubscriptionType | null;
  /** Manual balance credit is SuperAdmin-only. */
  canManualCredit: boolean;
  onUpdated: () => void;
};

/**
 * Consolidated "money knobs" for the plan-management page: read-only balance +
 * usage, opt-in auto top-up (auto-renewal) settings with a no-card warning, and
 * (SuperAdmin only) a manual balance credit. Auto top-up here goes through the
 * scoped `updateAutoRenewal` procedure, so it works for partners on managed
 * clients too. The customer keeps the same settings on their `/payments` page.
 */
export function BalanceAndTopUpCard({
  organizationId,
  organization,
  subscription,
  canManualCredit,
  onUpdated,
}: BalanceAndTopUpCardProps) {
  const { t } = useLingui();
  const currency = organization.currency ?? Currency.CZK;

  const isPaidSub = Boolean(
    subscription && subscription.tier !== SubscriptionTier.Free,
  );

  const { data: paymentMethod } =
    trpc.subscriptions.hasDefaultPaymentMethod.useQuery(
      { organizationId },
      { enabled: isPaidSub },
    );
  const hasCard = paymentMethod?.hasDefaultPaymentMethod ?? false;

  const updateAutoRenewalMutation =
    trpc.subscriptions.updateAutoRenewal.useMutation({
      onSuccess: () => {
        toast.success(t`Auto top-up settings updated.`);
        onUpdated();
      },
      onError: error => {
        toast.error(t`Failed to update auto top-up.`, {
          description: error.message,
        });
      },
    });

  const [manualAmount, setManualAmount] = useState('');
  const [manualReason, setManualReason] = useState('');

  const addManualBalanceMutation =
    trpc.subscriptions.addManualBalance.useMutation({
      onSuccess: () => {
        toast.success(t`Balance credited.`);
        setManualAmount('');
        setManualReason('');
        onUpdated();
      },
      onError: error => {
        toast.error(t`Failed to credit balance.`, {
          description: error.message,
        });
      },
    });

  const handleManualCredit = () => {
    const amount = Number.parseFloat(manualAmount);

    if (Number.isNaN(amount) || amount <= 0) {
      toast.error(t`Enter a positive amount.`);

      return;
    }

    addManualBalanceMutation.mutate({
      organizationId,
      amountToAdd: amount,
      currency,
      reason: manualReason.trim() || undefined,
    });
  };

  return (
    <Card title={t`Balance & auto top-up`} className='h-full'>
      <div className='space-y-6'>
        <UsageMetricsDisplay
          organization={organization}
          subscription={subscription}
        />

        {isPaidSub && subscription ? (
          <section className='space-y-3 border-t pt-4'>
            <h3 className='text-sm font-medium'>
              <Trans>Automatic top-up</Trans>
            </h3>

            {!hasCard && (
              <Alert
                variant='warning'
                title={<Trans>No saved card on file</Trans>}
                description={
                  <Trans>
                    Automatic top-up charges the customer&apos;s saved card.
                    This customer has none (e.g. they pay by bank transfer), so
                    enabling it here has no effect until a card is saved.
                  </Trans>
                }
              />
            )}

            <AutoRenewalSettings
              subscription={subscription}
              currency={currency}
              isSaving={updateAutoRenewalMutation.isPending}
              onSave={async values => {
                await saveAutoRenewal(updateAutoRenewalMutation, {
                  organizationId,
                  values,
                });
              }}
              onUpdate={onUpdated}
            />
          </section>
        ) : (
          <p className='text-muted-foreground border-t pt-4 text-sm'>
            <Trans>
              Automatic top-up is available once the organization is on an
              active paid plan.
            </Trans>
          </p>
        )}

        {canManualCredit && (
          <section className='space-y-3 border-t pt-4'>
            <h3 className='text-sm font-medium'>
              <Trans>Manual balance credit</Trans>
            </h3>
            <p className='text-muted-foreground text-xs'>
              <Trans>
                Adds credit to the prepaid balance directly (no charge). Use to
                compensate or correct. SuperAdmin only.
              </Trans>
            </p>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <div className='space-y-1'>
                <Label htmlFor='manual-credit-amount'>
                  <Trans>Amount ({currency.toUpperCase()})</Trans>
                </Label>
                <Input
                  id='manual-credit-amount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={manualAmount}
                  onChange={e => setManualAmount(e.target.value)}
                  disabled={addManualBalanceMutation.isPending}
                />
              </div>
              <div className='space-y-1'>
                <Label htmlFor='manual-credit-reason'>
                  <Trans>Reason (optional)</Trans>
                </Label>
                <Input
                  id='manual-credit-reason'
                  type='text'
                  value={manualReason}
                  onChange={e => setManualReason(e.target.value)}
                  disabled={addManualBalanceMutation.isPending}
                />
              </div>
            </div>
            <Button
              size='sm'
              className='self-start'
              loading={addManualBalanceMutation.isPending}
              onClick={handleManualCredit}
            >
              <Trans>Credit balance</Trans>
            </Button>
          </section>
        )}
      </div>
    </Card>
  );
}

/**
 * Bridges the settings component's base-credit values to the scoped
 * `updateAutoRenewal` mutation.
 */
async function saveAutoRenewal(
  mutation: ReturnType<typeof trpc.subscriptions.updateAutoRenewal.useMutation>,
  params: { organizationId: string; values: AutoRenewalUpdateValues },
): Promise<void> {
  await mutation.mutateAsync({
    organizationId: params.organizationId,
    ...params.values,
  });
}
